import type { LogLevel, LogMeta } from './types'
import { shouldLog } from './levels'
import { formatLog } from './formatters'
import { getState } from './state'
import { handleSentry, addBreadcrumb } from './sentry'
import { handleElastic } from './elastic'
import { redactMeta, redactString } from './redact'

// Internal keys that should not be forwarded as console meta
const INTERNAL_KEYS = new Set(['sentry', 'index', 'error'])

function cleanMeta(meta?: LogMeta): Record<string, unknown> | undefined {
  if (!meta) return undefined
  const cleaned: Record<string, unknown> = {}
  let hasKeys = false
  for (const key of Object.keys(meta)) {
    if (!INTERNAL_KEYS.has(key)) {
      cleaned[key] = meta[key]
      hasKeys = true
    }
  }
  return hasKeys ? cleaned : undefined
}

export class Logger {
  private ns: string

  constructor(namespace: string) {
    this.ns = namespace
  }

  debug(message: string, meta?: LogMeta): void {
    this.log('debug', message, meta)
  }

  info(message: string, meta?: LogMeta): void {
    this.log('info', message, meta)
  }

  warn(message: string, meta?: LogMeta): void {
    this.log('warn', message, meta)
  }

  error(message: string, meta?: LogMeta): void {
    this.log('error', message, meta)
  }

  setUser(user: { id?: string; email?: string; [key: string]: unknown }): void {
    const state = getState()
    if (state.sentry) {
      state.sentry.setUser(user)
    }
  }

  setTags(tags: Record<string, string>): void {
    const state = getState()
    if (state.sentry) {
      state.sentry.setTags(tags)
    }
  }

  setExtras(extras: Record<string, unknown>): void {
    const state = getState()
    if (state.sentry) {
      state.sentry.setExtras(extras)
    }
  }

  private log(level: LogLevel, message: string, meta?: LogMeta): void {
    const state = getState()

    // Mask secrets once at the entry point so all destinations (breadcrumbs,
    // Sentry events, ES tracking docs, console/CloudWatch) get the safe form.
    const safeMessage = redactString(message)
    const safeMeta = redactMeta(meta)

    // Sentry breadcrumb for info, warn, error (regardless of log level filtering)
    if (level !== 'debug') {
      addBreadcrumb(state.sentry, level, this.ns, safeMessage, cleanMeta(safeMeta))
    }

    // Sentry event if sentry: true
    if (safeMeta?.sentry) {
      handleSentry(state.sentry, level, safeMessage, safeMeta)
    }

    // ES tracking index if index: true
    if (safeMeta?.index) {
      handleElastic(state.elastic, state.provider, state.stage, state.version, this.ns, level, safeMessage, safeMeta)
    }

    // Console output respects log level
    if (!shouldLog(level, state.level)) {
      return
    }

    const displayMeta = cleanMeta(safeMeta)
    const formatted = formatLog(level, this.ns, safeMessage, displayMeta)

    switch (level) {
      case 'debug':
        console.debug(formatted)
        break
      case 'info':
        console.log(formatted)
        break
      case 'warn':
        console.warn(formatted)
        break
      case 'error':
        console.error(formatted)
        if (safeMeta?.error) {
          console.error(safeMeta.error)
        }
        break
    }
  }
}

export function createLogger(namespace: string): Logger {
  return new Logger(namespace)
}
